import {Injectable} from '@angular/core';
import {BaseApiService} from '../base-api.service';
import {EnterpriseSettings} from '../../../model/enterprise-settings';
import {EnterpriseProfile} from '../../../model/enterprise-profile';
import {EnterpriseTeam} from '../../../model/enterprise-team';

/**
 * Organization settings that reach other accounts.
 *
 * The owner's browser used to write these straight to Firestore -- onto the
 * members' own user documents and onto their projects. The rules no longer let
 * one account write another's document, so the server does it: it checks the
 * caller is the owner (or an Admin), validates the input and records it in the
 * activity log.
 */
/** The organisation as its owner, Admins and Managers see it (GET /organization). */
export interface OrganizationView {
  /** Its owner's uid. */
  orgUid: string;
  isOwner: boolean;
  /** The caller's role; '' for the owner. */
  role: string;
  /** May change the profile, policy, teams and sign-in (owner or Admin). */
  canManage: boolean;
  name: string;
  profile: EnterpriseProfile | null;
  settings: EnterpriseSettings | null;
  teams: EnterpriseTeam[];
  /** The single sign-on provider id, '' when none. */
  provider: string;
  memberUids: string[];
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationApiService extends BaseApiService {

  constructor() {
    super();
  }

  /** The organisation the caller works for (owner, Admin or Manager). */
  getOrganization(): Promise<OrganizationView> {
    return this.get('/organization');
  }

  /**
   * Saves the organization profile, its policy, or both; a half left out is
   * not touched. A saved profile's name and logo are copied onto every member
   * (what their dashboard shows). Returns both halves as stored.
   */
  saveProfile(changes: {settings?: EnterpriseSettings, profile?: EnterpriseProfile})
    : Promise<{settings: EnterpriseSettings, profile: EnterpriseProfile}> {
    return this.put('/organization/profile', changes);
  }

  /**
   * Adds a team, or replaces the one with the same id (rename, colour,
   * territory). A renamed team is refreshed on its projects. Returns the
   * organisation's whole list as stored, other people's changes included.
   */
  upsertTeam(team: EnterpriseTeam): Promise<{teams: EnterpriseTeam[]}> {
    return this.put('/organization/teams', {upsert: team});
  }

  /**
   * Deletes a team: it is detached from its projects (access is kept) and
   * from its members. Returns the organisation's list as stored.
   */
  removeTeam(teamId: string): Promise<{teams: EnterpriseTeam[]}> {
    return this.put('/organization/teams', {remove: teamId});
  }

  /**
   * Sets which teams a member is on. The server also grants/removes their
   * access to the projects those teams are assigned to.
   */
  setMemberTeams(uid: string, teamIds: string[]): Promise<{teamIds: string[]}> {
    return this.put(`/organization/members/${encodeURIComponent(uid)}/teams`, {teamIds});
  }
}
